
import { useState, useEffect, useRef, useCallback } from 'react';
import { urbanArtworks, urbanCategories } from '../../../mocks/urbanArt';
import GalleryLightbox from './GalleryLightbox';

function AnimatedCard({ children, delay }: { children: React.ReactNode; delay: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(element);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -30px 0px' }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="transition-all duration-700 ease-out"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.95)',
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

export default function GalleryGrid() {
  const [activeCategory, setActiveCategory] = useState('Todos');
  const [selectedWork, setSelectedWork] = useState<typeof urbanArtworks[0] | null>(null);
  const [animationKey, setAnimationKey] = useState(0);

  const handleCategoryChange = useCallback((cat: string) => {
    setActiveCategory(cat);
    setAnimationKey(prev => prev + 1);
  }, []);

  const filteredWorks = activeCategory === 'Todos'
    ? urbanArtworks
    : urbanArtworks.filter(w => w.category === activeCategory);

  return (
    <section className="py-16 md:py-24 bg-neutral-950 relative">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Filtros */}
        <div className="flex items-center justify-center gap-2 md:gap-3 mb-10 md:mb-16 flex-wrap">
          {urbanCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => handleCategoryChange(cat)}
              className={`px-4 md:px-6 py-2 md:py-2.5 text-xs md:text-sm font-medium tracking-wider uppercase transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat
                  ? 'bg-amber-600 text-white'
                  : 'bg-neutral-800/60 text-neutral-400 hover:bg-neutral-800 hover:text-white border border-neutral-700'
              }`}
              style={{ clipPath: 'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)' }}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Contador */}
        <div className="flex items-center justify-between mb-8 md:mb-10">
          <p className="text-neutral-500 text-xs md:text-sm">
            Mostrando <strong className="text-white">{filteredWorks.length}</strong> {filteredWorks.length === 1 ? 'obra' : 'obras'}
          </p>
          <div className="flex items-center gap-2">
            <div className="w-6 md:w-8 h-0.5 bg-amber-600/50"></div>
            <i className="ri-palette-line text-amber-600 text-sm"></i>
            <div className="w-6 md:w-8 h-0.5 bg-amber-600/50"></div>
          </div>
        </div>

        {/* Grid de obras con animación */}
        <div key={animationKey} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
          {filteredWorks.map((work, index) => (
            <AnimatedCard key={`${work.id}-${animationKey}`} delay={index * 120}>
              <article
                className="group relative bg-neutral-900 rounded-lg overflow-hidden cursor-pointer border border-neutral-800 hover:border-amber-600/40 transition-all duration-500"
                onClick={() => setSelectedWork(work)}
              >
                <div className="relative w-full h-64 sm:h-72 md:h-80 overflow-hidden">
                  <img
                    src={work.image}
                    alt={`${work.title} - Arte urbano de Daro Tattoo`}
                    title={`${work.title} - ${work.technique}`}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                  {/* Overlay con icono */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-amber-600 rounded-full transform scale-50 group-hover:scale-100 transition-transform duration-500">
                      <i className="ri-zoom-in-line text-white text-lg md:text-xl"></i>
                    </div>
                  </div>

                  {/* Badge categoría */}
                  <div className="absolute top-3 md:top-4 left-3 md:left-4">
                    <span className="px-2.5 md:px-3 py-1 bg-black/70 backdrop-blur-sm text-amber-500 text-xs font-bold uppercase tracking-wider">
                      {work.category}
                    </span>
                  </div>
                </div>

                <div className="p-4 md:p-6">
                  <div className="flex items-start justify-between mb-2 md:mb-3">
                    <h3
                      className="text-lg md:text-xl font-bold text-white group-hover:text-amber-500 transition-colors"
                      style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '1px' }}
                    >
                      {work.title}
                    </h3>
                    <span className="text-neutral-600 text-xs mt-1">{work.year}</span>
                  </div>
                  <p className="text-neutral-500 text-xs md:text-sm leading-relaxed line-clamp-2 mb-3 md:mb-4">
                    {work.description}
                  </p>
                  <div className="flex items-center gap-2 text-neutral-600 text-xs">
                    <i className="ri-brush-line"></i>
                    <span>{work.technique}</span>
                  </div>
                </div>
              </article>
            </AnimatedCard>
          ))}
        </div>

        {/* Sin resultados */}
        {filteredWorks.length === 0 && (
          <div className="text-center py-20">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-neutral-800 rounded-full">
              <i className="ri-image-line text-neutral-600 text-2xl"></i>
            </div>
            <p className="text-neutral-500 text-lg">No hay obras en esta categoría todavía.</p>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {selectedWork && (
        <GalleryLightbox
          work={selectedWork}
          onClose={() => setSelectedWork(null)}
        />
      )}
    </section>
  );
}
